import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartApi, formatApiError } from '../lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0 });
      setInitialized(false);
      return;
    }
    setLoading(true);
    try {
      const response = await cartApi.get();
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (item) => {
    try {
      await cartApi.add(item);
      await fetchCart();
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const updateQuantity = async (item) => {
    try {
      await cartApi.update(item);
      await fetchCart();
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const removeFromCart = async (productId, size, color) => {
    try {
      await cartApi.remove(productId, size, color);
      await fetchCart();
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const clearCart = async () => {
    try {
      await cartApi.clear();
      setCart({ items: [], total: 0 });
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error) };
    }
  };

  const value = {
    cart,
    loading,
    initialized,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart,
    itemCount: cart.items.reduce((acc, item) => acc + item.quantity, 0),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
